import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Star, Download, Smartphone, HardDrive, Tag, ShieldCheck, ArrowRight, Info, Clock } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { getCatalogApp, isAppAvailable } from "@/lib/catalog";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PAYMENT_METHODS, PAYMENT_METHOD_KEYS } from "@/lib/payments";
import { downloadApp } from "@/lib/downloads";
import InstallInstructions from "@/components/InstallInstructions";

export default function AppDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    const fallbackApp = getCatalogApp(id);
    if (fallbackApp) setApp(fallbackApp);
    if (!fallbackApp) navigate("/");
  }, [id, navigate]);

  if (!app) return <div className="min-h-[60vh] grid place-items-center text-zinc-500">ဖွင့်နေသည်…</div>;
  const free = app.price_mmk === 0;
  const available = isAppAvailable(app);
  const platformLabel = {
    android: "Android (.apk)",
    ios: "iOS (.ipa)",
  }[app.platform] || app.platform;

  const handleBuy = () => {
    if (!available) {
      toast.info("ဤဗားရှင်း မကြာမီ ရနိုင်ပါမည်။");
      return;
    }
    if (!user) { toast.info("ဆက်လုပ်ရန် ဝင်ရောက်ပါ"); navigate("/login", { state: { from: `/apps/${id}` } }); return; }
    if (free) { handleDownload(); return; }
    navigate(`/checkout/${id}`);
  };

  const handleDownload = async () => {
    const started = await downloadApp(app);
    if (started) {
      toast.success("Download စတင်နေသည်…");
      if (app.platform === "android") setShowInstall(true);
    } else {
      toast.info("ဝယ်ယူပြီးနောက် Dashboard တွင် Download ဆွဲနိုင်ပါမည်။");
    }
  };

  const paymentLabels = PAYMENT_METHOD_KEYS.map((k) => PAYMENT_METHODS[k].label).join(" သို့မဟုတ် ");

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/apps" className="text-sm text-zinc-500 hover:text-zinc-900">← Back to apps</Link>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="bg-white border border-zinc-200 rounded-3xl p-6">
            <div className="aspect-square rounded-2xl bg-zinc-100 overflow-hidden border border-zinc-200">
              {app.icon_url ? (
                <img src={fileUrl(app.icon_url)} alt={app.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-6xl font-display font-black text-zinc-300">
                  {app.title?.[0]}
                </div>
              )}
            </div>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-medium">{app.category}</div>
              <h1 className="font-display font-bold text-2xl mt-1">{app.title}</h1>
              <p className="text-sm text-zinc-600 mt-1">{app.developer}</p>

              {!available && (
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-zinc-100 px-2.5 py-1 text-xs font-semibold text-zinc-600">
                  <Clock className="w-3.5 h-3.5" /> မကြာမီ ရနိုင်ပါမည်
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mt-5 text-center">
                <div className="bg-zinc-50 rounded-lg p-3">
                  <Star className="w-4 h-4 mx-auto fill-amber-400 text-amber-400" />
                  <div className="font-display font-semibold mt-1 tabular-nums">{app.rating?.toFixed(1)}</div>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3">
                  <Download className="w-4 h-4 mx-auto text-zinc-700" />
                  <div className="font-display font-semibold mt-1 tabular-nums text-sm">{(app.downloads || 0).toLocaleString()}</div>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3">
                  <HardDrive className="w-4 h-4 mx-auto text-zinc-700" />
                  <div className="font-display font-semibold mt-1 text-sm">{app.size_mb} MB</div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-zinc-100">
                <div className="text-xs tracking-wide text-zinc-500">စျေးနှုန်း</div>
                <div className="font-bold text-3xl mt-1">
                  {free ? <span className="text-emerald-600">အခမဲ့</span> : (
                    <span className="tabular-nums">{app.price_mmk.toLocaleString()} <span className="text-base text-zinc-500">ကျပ်</span></span>
                  )}
                </div>
                {available ? (
                  <Button
                    data-testid="buy-now-btn"
                    onClick={handleBuy}
                    className="w-full mt-4 h-12 bg-[#7CC576] hover:bg-[#5FB259] rounded-lg text-base"
                  >
                    {free ? "အခမဲ့ ရယူရန်" : "ဝယ်ယူရန်"} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    data-testid="coming-soon-btn"
                    disabled
                    className="w-full mt-4 h-12 rounded-lg text-base bg-zinc-200 text-zinc-500 hover:bg-zinc-200"
                  >
                    မကြာမီ ရနိုင်ပါမည်
                  </Button>
                )}
                {available && !free && (
                  <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1.5 justify-center">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> {paymentLabels} ဖြင့် ပေးချေပါ
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          <Tabs defaultValue="about" className="bg-white border border-zinc-200 rounded-3xl p-6">
            <TabsList className="bg-zinc-100">
              <TabsTrigger value="about" data-testid="tab-about">အကြောင်း</TabsTrigger>
              <TabsTrigger value="details" data-testid="tab-details">အသေးစိတ်</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews">သုံးသပ်ချက်</TabsTrigger>
            </TabsList>
            <TabsContent value="about" className="pt-6">
              <h2 className="font-display font-bold text-2xl">{app.short_description}</h2>
              <p className="mt-4 text-zinc-700 leading-relaxed whitespace-pre-line">{app.description}</p>
            </TabsContent>
            <TabsContent value="details" className="pt-6 space-y-4">
              <Detail label="ဗားရှင်း" value={app.version} icon={<Tag className="w-4 h-4" />} />
              <Detail label="Platform" value={platformLabel} icon={<Smartphone className="w-4 h-4" />} />
              <Detail label="အရွယ်အစား" value={`${app.size_mb} MB`} icon={<HardDrive className="w-4 h-4" />} />
              <Detail label="ဖန်တီးသူ" value={app.developer} />
              <Detail label="အမျိုးအစား" value={app.category} />
              <Detail label="ထုတ်ဝေသည့်နေ့" value={new Date(app.created_at).toLocaleDateString()} />
            </TabsContent>
            <TabsContent value="reviews" className="pt-6">
              <div className="text-zinc-500 text-sm">သုံးသပ်ချက်များ မကြာမီ ထည့်သွင်းပါမည်။ ဝယ်ယူပြီးနောက် ပထမဆုံး သုံးသပ်သူ ဖြစ်လိုက်ပါ။</div>
            </TabsContent>
          </Tabs>

          {/* Android install info banner */}
          {app.platform === "android" && (
            <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[#7CC576] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-zinc-900">Android ထည့်သွင်းနည်း (Sideload)</div>
                  <p className="text-sm text-zinc-600 mt-1">
                    ဝယ်ယူပြီးနောက် APK ကို Download ဆွဲပြီး ဖုန်းပေါ်သို့ ကိုယ်တိုင်ထည့်သွင်းရပါမည်။ Android 7.0+ လိုအပ်ပါသည်။
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowInstall(true)}
                    className="mt-3 rounded-lg border-[#7CC576] text-[#5FB259] hover:bg-[#7CC576]/10"
                  >
                    <Smartphone className="w-4 h-4 mr-2" /> အသေးစိတ် ထည့်သွင်းနည်း ကြည့်ရန်
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <InstallInstructions
        open={showInstall}
        onOpenChange={setShowInstall}
        app={app}
        onDownload={free && available ? handleDownload : undefined}
      />
    </div>
  );
}

function Detail({ label, value, icon }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-zinc-100 last:border-0">
      <div className="flex items-center gap-2 text-sm text-zinc-500">
        {icon}
        <span className="uppercase tracking-[0.14em] text-xs font-medium">{label}</span>
      </div>
      <div className="font-medium text-zinc-900">{value}</div>
    </div>
  );
}